import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { shared } from "../styles.js";
import { formatBytes, relativeTime, shortDigest } from "../lib/format.js";
import { fetchResourceBlob, type ResourceRecord } from "../api/registry.js";
import { humanError } from "../api/errors.js";

const MIME_LABELS: Record<string, string> = {
  "image/png": "PNG",
  "image/jpeg": "JPG",
  "image/webp": "WEBP",
  "image/gif": "GIF",
  "text/csv": "CSV",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
  "application/vnd.apache.parquet": "PARQUET",
};

/**
 * resources-panel is a pure, read-only display of one version's bundled
 * binary resources (images for the Asset kind, csv/xlsx/parquet for
 * DataSource kinds), plus a Download action available to every viewer.
 * Resources are immutable once their version publishes — upload/replace/
 * delete happen only as part of a `bino publish`, never here.
 */
@customElement("resources-panel")
export class ResourcesPanel extends LitElement {
  static override styles = [
    shared,
    css`
      .row {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 9px 0;
        border-bottom: 1px solid var(--border);
      }
      .type {
        flex: none;
        font: 600 10px/1 var(--mono);
      }
      .tx {
        flex: 1;
        min-width: 0;
      }
      .nm {
        font: 500 13px/1.4 var(--mono);
        color: var(--text);
        overflow-wrap: anywhere;
      }
      .meta {
        font: 400 11px/1.4 var(--sans);
        color: var(--text-faint);
      }
      .act {
        flex: none;
        font-size: 11px;
        padding: 5px 8px;
      }
    `,
  ];

  @property({ attribute: false }) resources: ResourceRecord[] = [];
  @property() scope = "";
  @property() name = "";
  @property() version = "";

  @state() private error = "";
  @state() private downloading = "";

  private typeLabel(mime: string): string {
    return MIME_LABELS[mime] ?? "FILE";
  }

  private async download(r: ResourceRecord) {
    this.error = "";
    this.downloading = r.name;
    try {
      const blob = await fetchResourceBlob(this.scope, this.name, this.version, r.name);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      this.error = humanError(e);
    } finally {
      this.downloading = "";
    }
  }

  private renderRow(r: ResourceRecord) {
    return html`<div class="row" data-row=${r.name}>
      <span class="chip type">${this.typeLabel(r.mime_type)}</span>
      <div class="tx">
        <div class="nm">${r.name}</div>
        <div class="meta">
          ${formatBytes(r.size)} · ${relativeTime(r.created)} ·
          <span class="mono">${shortDigest(r.content_hash)}</span>
        </div>
      </div>
      <button
        class="btn act"
        type="button"
        ?disabled=${this.downloading === r.name}
        @click=${() => this.download(r)}
      >
        ${this.downloading === r.name ? "Downloading…" : "Download"}
      </button>
    </div>`;
  }

  override render() {
    return html`
      ${this.error ? html`<div class="error">${this.error}</div>` : nothing}
      ${this.resources.length
        ? this.resources.map((r) => this.renderRow(r))
        : html`<p class="muted">No resources for this version.</p>`}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "resources-panel": ResourcesPanel;
  }
}
